import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import CloudUploadIconDuotone from '@/components/icons/CloudUploadIconDuotone';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Button } from '../ui/button';
import Delete_02_Icon from '../icons/Delete_02_Icon';
import { ApiConfig } from '@/config/api.config';
import { ImagesHelper } from '@/utils/ImagesHelper';

type Props<T extends {}> = {
	label?: string;
	optional?: boolean;
	type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
} & FormInputProps<T>;

const EImageUploaderForm = <T extends {}>({
	label,
	optional = false,
	fieldName,
	control,
	className,
	containerClassName,
	disabled,
}: Props<T>) => {
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const { t } = useTranslation();

	const handleInputRef = () => {
		if (inputRef.current) inputRef.current.click();
	};

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			{label && (
				<h4 className='text-sm font-medium'>
					{label}
					{optional && (
						<span className='text-popover-600'> (optional)</span>
					)}
				</h4>
			)}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange },
					fieldState,
					formState,
				}) => {
					const handleChangeFile = (file: File) => {
						if (file) {
							onChange(file);
						}
					};

					const handleFileInputChange = (
						e: React.ChangeEvent<HTMLInputElement>,
					) => {
						const file = e.target.files?.[0];
						if (file) {
							handleChangeFile(file);
						}
					};

					const handleDragOver = (
						e: React.DragEvent<HTMLDivElement>,
					) => {
						e.preventDefault();
						e.stopPropagation();
						setIsDragging(true);
					};

					const handleDragLeave = () => {
						setIsDragging(false);
					};

					const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
						e.preventDefault();
						e.stopPropagation();

						const file = e.dataTransfer.files?.[0];
						if (file) {
							handleChangeFile(file);
						}
					};

					const typedValue = value as File | string;

					const imagePath = ImagesHelper.getImageUrlFromFile(typedValue);

					return (
						<>
							<div className='relative'>
								<div
									className={cn(
										'w-full h-50 flex flex-col items-center justify-center gap-2 bg-popover-10 border border-dashed border-popover-100 rounded-semi-xl cursor-pointer select-none overflow-hidden transition-all hover:bg-primary-color-50',
										fieldState.invalid
											? 'border-error-1000'
											: '',
										isDragging ? 'bg-popover-50' : '',
										className,
									)}
									onClick={value ? undefined : handleInputRef}
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
									onDrop={handleDrop}>
									{value ? (
										<img
											src={imagePath}
											alt='Profile picture'
											className='w-full h-full object-cover'
											onClick={handleInputRef}
										/>
									) : (
										<>
											<CloudUploadIconDuotone className='size-8' />
											<div className='flex flex-col items-center gap-1'>
												<p className='text-sm font-medium'>
													{t('Drop photo here or')}{' '}
													<span className='underline'>
														{t('Browse')}
													</span>
												</p>
												<p className='text-sm text-popover-600'>
													{t('Max. file size 10MB')}
												</p>
											</div>
										</>
									)}
								</div>
								<input
									type='file'
									className={'hidden'}
									ref={inputRef}
									onChange={handleFileInputChange}
									disabled={disabled || formState.disabled}
								/>
								{value && (
									<Button
										variant='outline'
										size='sm'
										className='absolute z-10 top-3.5 right-3.5'
										onClick={() => onChange('')}>
										<Delete_02_Icon />
									</Button>
								)}
							</div>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</>
					);
				}}
			/>
		</div>
	);
};

export default EImageUploaderForm;
